import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import Markdown from "@/components/Markdown";
import { Bold, Italic, Code, Image as ImageIcon, Link as LinkIcon, List, ListOrdered, Quote, Heading, FileCode2 } from "lucide-react";

/**
 * MarkdownEditor: split-view rich editor with a compact toolbar.
 * value: string, onChange: (str) => void
 */
export default function MarkdownEditor({ value, onChange, minRows = 12 }) {
  const areaRef = useRef(null);

  const wrap = (before, after = before, placeholder = "") => {
    const el = areaRef.current;
    if (!el) return;
    const start = el.selectionStart, end = el.selectionEnd;
    const sel = value.slice(start, end) || placeholder;
    const next = value.slice(0, start) + before + sel + after + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      const cursor = start + before.length + sel.length;
      el.setSelectionRange(cursor, cursor);
    });
  };

  const prefixLine = (prefix) => {
    const el = areaRef.current;
    if (!el) return;
    const start = el.selectionStart, end = el.selectionEnd;
    const before = value.slice(0, start);
    const lineStart = before.lastIndexOf("\n") + 1;
    const next = value.slice(0, lineStart) + prefix + value.slice(lineStart);
    onChange(next);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(end + prefix.length, end + prefix.length);
    });
  };

  const insertImage = () => {
    const url = window.prompt("Image URL");
    if (!url) return;
    const alt = window.prompt("Alt text (optional)") || "";
    wrap(`![${alt}](${url})`, "");
  };
  const insertLink = () => {
    const url = window.prompt("URL");
    if (!url) return;
    const el = areaRef.current;
    const sel = el ? value.slice(el.selectionStart, el.selectionEnd) : "";
    wrap(`[${sel || "link"}](${url})`, "");
  };
  const insertCodeBlock = () => {
    const lang = window.prompt("Language (e.g., bash, js, python)", "bash") || "";
    wrap(`\n\`\`\`${lang}\n`, "\n```\n", "your code here");
  };

  const Btn = ({ onClick, icon: Icon, label, testid }) => (
    <button
      type="button"
      onClick={onClick}
      title={label}
      className="h-8 w-8 flex items-center justify-center rounded border border-border/60 text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors"
      data-testid={testid}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1 flex-wrap">
        <Btn onClick={() => wrap("**", "**", "bold")} icon={Bold} label="Bold" testid="md-bold" />
        <Btn onClick={() => wrap("_", "_", "italic")} icon={Italic} label="Italic" testid="md-italic" />
        <Btn onClick={() => prefixLine("# ")} icon={Heading} label="Heading" testid="md-heading" />
        <Btn onClick={() => prefixLine("- ")} icon={List} label="Bullet list" testid="md-list" />
        <Btn onClick={() => prefixLine("1. ")} icon={ListOrdered} label="Numbered list" testid="md-olist" />
        <Btn onClick={() => prefixLine("> ")} icon={Quote} label="Quote" testid="md-quote" />
        <Btn onClick={() => wrap("`", "`", "code")} icon={Code} label="Inline code" testid="md-code" />
        <Btn onClick={insertCodeBlock} icon={FileCode2} label="Code block" testid="md-codeblock" />
        <Btn onClick={insertImage} icon={ImageIcon} label="Image" testid="md-image" />
        <Btn onClick={insertLink} icon={LinkIcon} label="Link" testid="md-link" />
      </div>
      <Tabs defaultValue="write">
        <TabsList>
          <TabsTrigger value="write" data-testid="md-tab-write">Write</TabsTrigger>
          <TabsTrigger value="preview" data-testid="md-tab-preview">Preview</TabsTrigger>
          <TabsTrigger value="split" data-testid="md-tab-split">Split</TabsTrigger>
        </TabsList>
        <TabsContent value="write" className="pt-2">
          <textarea
            ref={areaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            rows={minRows}
            className="w-full font-mono text-[13px] bg-card border border-border/60 rounded-md p-3 resize-y focus:outline-none focus:border-primary/50"
            data-testid="md-textarea"
            placeholder="Write in Markdown. Use the toolbar for images, links and code blocks."
          />
        </TabsContent>
        <TabsContent value="preview" className="pt-2">
          <div className="p-4 rounded-md border border-border/60 bg-card min-h-[280px]">
            <Markdown>{value || "_Nothing to preview yet._"}</Markdown>
          </div>
        </TabsContent>
        <TabsContent value="split" className="pt-2">
          <div className="grid grid-cols-2 gap-3">
            <textarea
              ref={areaRef}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              rows={minRows}
              className="font-mono text-[13px] bg-card border border-border/60 rounded-md p-3 resize-y focus:outline-none focus:border-primary/50"
            />
            <div className="p-4 rounded-md border border-border/60 bg-card overflow-auto max-h-[600px]">
              <Markdown>{value || "_Nothing to preview yet._"}</Markdown>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
